import { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Button from '../reusable/Button/Button';
import { useUpdateAccountMutation } from '../store/api/dashboardApi';
import { useGetAccountBreakdownQuery } from '../store/api/metricsApi';
import { authErrorMessage } from '../store/api/authHelpers';
import { formatLoginId } from '../utilities/accounts';
import { NA } from '../utilities/format';
import { t } from '../i18n/i18n';
import './ShareDialog.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The public-profile link for one account, off the app's own origin so the
 * preview opens the real `/p/:accountId` route rather than a stand-in host.
 * The platform rides along because the public endpoint is asked for it.
 *
 * The login is encoded: TradeLocker's carry a "#" ("D#2568704"), and left raw
 * everything after it became the URL's fragment — the page read "D" and lost
 * the platform with it.
 */
const shareLink = (accountId, platform) =>
  accountId
    ? `${window.location.origin}/p/${encodeURIComponent(accountId)}${
        platform ? `?platform=${encodeURIComponent(platform)}` : ''
      }`
    : '';

/**
 * "Public Page" in the top bar — 1:32487's share sheet.
 *
 * The link is derived from the account rather than issued, so there is no
 * token to rotate: what makes it resolve is the account's own `public` flag,
 * which is what the two buttons here write. Turning it off breaks the link for
 * everyone it was sent to, which is the whole of "revoking" it.
 */
export default function ShareDialog({ open, accountId, account, onClose }) {
  const platform = account?.platform;
  const link = shareLink(accountId, platform);
  const [updateAccount, { isLoading: isSaving }] = useUpdateAccountMutation();

  // `public` off the account's own record, not off the picker's list row —
  // `account_information` is where the flag actually lives. Only while the
  // sheet is open: nothing else on the shell asks whether sharing is on. The
  // Simple category, so Account Metrics' own Simple request answers it.
  const { data: metrics, isLoading: isChecking } = useGetAccountBreakdownQuery(
    { loginId: accountId, platform, category: 'simple' },
    { skip: !open || !accountId }
  );
  const details = metrics?.account_information;

  // The record leads, except in the moment between a toggle landing and the
  // refetch it invalidated coming back — where the old value would otherwise
  // flip the button back and forth.
  const [toggled, setToggled] = useState(null);
  const isPublic = toggled ?? Boolean(details?.public);

  useEffect(() => setToggled(null), [accountId, open]);

  const setPublic = async (next) => {
    try {
      await updateAccount({ loginId: accountId, public: next }).unwrap();
      setToggled(next);
      message.success(next ? t('Public page is live') : t('Public page turned off'));
    } catch (error) {
      message.error(
        next ? t('Could not publish this account') : t('Could not turn sharing off'),
        authErrorMessage(error)
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
      destroyOnHidden
      className="share-dialog"
      rootClassName="share-dialog-root"
      aria-label={t('Share performance')}
    >
      <div className="share-dialog__body">
        <h2 className="share-dialog__title">
          {t('Share performance')}
          <span className="share-dialog__sep" />
          <span className="share-dialog__account">
            {accountId ? formatLoginId(accountId) : NA}
          </span>
        </h2>

        <p className="share-dialog__text">
          {t(
            'Anyone with the link sees equity, objectives, closed trades and payout history — read only. Credentials, email, payment methods and reference numbers are never included.',
          )}
        </p>

        <div className={`share-dialog__status${isPublic && !isChecking ? '' : ' is-off'}`}>
          <span className="share-dialog__dot" />
          {isChecking && t('Checking whether this account is shared…')}
          {!isChecking &&
            (isPublic
              ? t('This account is public — the link below works for anyone.')
              : t('This account is private. Nobody can open the link until you publish it.'))}
        </div>

        <div className="share-dialog__link">
          <input
            className="share-dialog__url"
            type="text"
            value={link}
            readOnly
            aria-label={t('Public page link')}
            onFocus={(e) => e.target.select()}
          />

          <CopyToClipboard
            text={link}
            onCopy={(_text, result) => {
              if (result) message.success(t('Link copied'));
              else message.error(t('Could not copy link'));
            }}
          >
            <Button
              variant="pill"
              height={38}
              paddingInline={14}
              disabled={!link || !isPublic || isChecking}
            >
              {t('Copy link')}
              <span className="icon share-dialog__copy-icon" style={icon('copy')} />
            </Button>
          </CopyToClipboard>
        </div>

        <div className="share-dialog__actions">
          <Button
            variant="ghost"
            href={isPublic && !isChecking ? link || undefined : undefined}
            target="_blank"
            rel="noreferrer"
            disabled={!link || !isPublic || isChecking}
          >
            {t('Open public page')}
          </Button>

          <Button
            variant={isPublic ? 'ghost' : 'pill'}
            height={isPublic ? undefined : 40}
            paddingInline={isPublic ? undefined : 20}
            disabled={!accountId || isSaving || isChecking}
            onClick={() => setPublic(!isPublic)}
          >
            {isSaving
              ? isPublic
                ? t('Turning off…')
                : t('Publishing…')
              : isPublic
                ? t('Turn off sharing')
                : t('Make public')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
